import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, Sparkles, Users, ClipboardList, CheckCircle, Clock, AlertTriangle, AlertCircle, Bell, BellRing, BellOff, Check, MapPin, Home, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AddHiveDialog from '../components/AddHiveDialog';
import AddYardDialog from '../components/AddYardDialog';
import { usePlanLimits } from '../hooks/usePlanLimits';
import EditYardDialog from '../components/EditYardDialog';
import BeekeeperMap from '../components/BeekeeperMap';
import TermsAcceptanceDialog from '../components/TermsAcceptanceDialog';
import { TERMS_VERSION } from '../components/TermsAcceptanceDialog';
import QRScanner from '../components/QRScanner';
import AIProgressReport from '../components/AIProgressReport';
import AllHivesAIReport from '../components/AllHivesAIReport';
import AppFooter from '../components/AppFooter';
import AdCarousel from '../components/AdCarousel';
import FindHoney from './FindHoney';
import FindBeeClub from './FindBeeClub';
import AdvertiserDashboard from './AdvertiserDashboard';
import CrewManagement from './CrewManagement';
import TeamChat from './TeamChat';
import BulkAddHives from './BulkAddHives';
import BulkSplit from './BulkSplit';
import QueenRearing from './QueenRearing';
import SwarmTraps from './SwarmTraps';
import SwarmCollectionsView from '../components/SwarmCollectionsView';
import SwarmCollectionSettings from '../components/SwarmCollectionSettings';
import Analytics from './Analytics';
import HiveComparison from './HiveComparison';
import HiveCosts from './HiveCosts';
import KnowledgeLibrary from './KnowledgeLibrary';
import Marketplace from './Marketplace';
import DIYIdeas from './DIYIdeas';
import BuilderSettings from './BuilderSettings';
import HiveMind from './HiveMind';
import MyProfile from './MyProfile';
import Subscription from './Subscription';
import UserManual from './UserManual';
import UserManagement from './UserManagement';
import AdminListings from './AdminListings';
import AdminSettings from './AdminSettings';
import AdminSubscriptionPlans from './AdminSubscriptionPlans';
import AdminConfiguration from './AdminConfiguration';
import AdminBugReports from './AdminBugReports';
import AdminLegalEditor from './AdminLegalEditor';
import AdminReviews from './AdminReviews';
import AdminHoneyRequests from './AdminHoneyRequests';
import AdminTours from './AdminTours';
import AdminAds from './AdminAds';
import AdminBeeClubs from './AdminBeeClubs';
import TourEngine from '../components/TourEngine';
import PageTourButton from '../components/PageTourButton';
import GettingStartedChecklist from '../components/GettingStartedChecklist';
import MerchStore from './MerchStore';
import AdminSwarmDashboard from './AdminSwarmDashboard';
import HiveDetail from './HiveDetail';
import InspectionForm from './InspectionForm';
import InspectionView from './InspectionView';
import CollectionDetail from './CollectionDetail';
import IdeaDetail from './IdeaDetail';
import BuilderProfile from './BuilderProfile';
import AddHarvestDialog from '../components/AddHarvestDialog';
import usePushNotifications from '../hooks/usePushNotifications';
import HarvestsView from '../components/HarvestsView';
import SalesView from '../components/SalesView';
import ProductsView from '../components/ProductsView';
import HarvestSettingsView from '../components/HarvestSettingsView';
import TreatmentPlanWizard from '../components/TreatmentPlanWizard';
import TreatmentPlansView from '../components/TreatmentPlansView';
import BulkWeightEntry from '../components/BulkWeightEntry';
import ManageHiveTypes from '../components/ManageHiveTypes';
import SeasonalReminders from '../components/SeasonalReminders';
import { QueenReminderAlerts } from '../components/queen/QueenReminderAlerts';
import { QueenlessAlerts } from '../components/queen/QueenlessAlerts';
import { MobileQuickActions } from '../components/mobile/MobileQuickActions';
import SeasonSettings from '../components/SeasonSettings';
import Donations from '../components/Donations';
import FrameScanDialog from '../components/FrameScanDialog';
import MiteCountDialog from '../components/MiteCountDialog';
import WeightConfigurationView from '../components/WeightConfigurationView';
import BulkYardActionDialog from '../components/dialogs/BulkYardActionDialog';
import YardHistoryDialog from '../components/dialogs/YardHistoryDialog';
import { 
  Sidebar,
  DashboardHeader,
  StatCardHorizontal,
  AttentionCenter,
  ThisWeek,
  PlannedActions,
  MonthlyCalendar,
  HappeningNowBar,
  HivesList,
  ApiariesList
} from '../components/dashboard';
import WeatherCard from '../components/dashboard/WeatherCard';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { toast } from 'sonner';
import { sendOrQueue, queuedToast } from '../utils/offlineStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feature gate wrapper — shows upgrade prompt for locked features
const UpgradeGate = ({ feature, hasFeature, children, featureName }) => {
  if (!feature || hasFeature(feature)) return children;
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-4 text-2xl">🔒</div>
      <h2 className="text-xl font-bold text-stone-800 mb-2">{featureName || 'Feature'} Requires an Upgrade</h2>
      <p className="text-stone-500 max-w-md mb-6">This feature isn't available on your current plan. Upgrade to unlock {featureName?.toLowerCase() || 'this feature'} and more.</p>
      <a href="/subscription" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
        View Plans & Upgrade
      </a>
    </div>
  );
};

const Dashboard = () => {
  const [hives, setHives] = useState([]);
  const [yards, setYards] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [inspectionSchedule, setInspectionSchedule] = useState({});
  const [pendingListingsCount, setPendingListingsCount] = useState(0);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [trapDataForHive, setTrapDataForHive] = useState(null); // Track trap when creating hive from swarm trap
  const [showAddYardDialog, setShowAddYardDialog] = useState(false);
  const { checkCanAdd, refresh: refreshLimits } = usePlanLimits();
  const { versionInfo, updateAvailable, refreshApp, dismissUpdate } = useVersionCheck();
  const [welcomeTour, setWelcomeTour] = useState(null);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);

  const handleAddYardClick = async () => {
    const check = await checkCanAdd('yards');
    if (check.allowed) setShowAddYardDialog(true);
  };

  const handleAddHiveClick = async () => {
    // Check if user has any apiaries first
    if (yards.length === 0) {
      toast.error(
        'You need to create an apiary first before adding hives.',
        {
          description: 'An apiary is the location where your hives are kept.',
          action: {
            label: 'Create Apiary',
            onClick: () => navigate('/?view=apiaries'),
          },
          duration: 8000,
        }
      );
      return;
    }
    const check = await checkCanAdd('hives');
    if (check.allowed) setShowAddDialog(true);
  };
  const [showEditYardDialog, setShowEditYardDialog] = useState(false);
  const [editingYard, setEditingYard] = useState(null);
  const [selectedYard, setSelectedYard] = useState(null);
  const [criticalHives, setCriticalHives] = useState([]);
  const [criticalAlertConfigs, setCriticalAlertConfigs] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [plannedActions, setPlannedActions] = useState([]);
  const [queenBatches, setQueenBatches] = useState([]);
  const [appSettings, setAppSettings] = useState(null);
  const [freePlatform, setFreePlatform] = useState(false);
  const [swarmTraps, setSwarmTraps] = useState([]);
  const [beekeepers, setBeekeepers] = useState([]);
  const [showBeekeeperMap, setShowBeekeeperMap] = useState(false);
  const [activityStatus, setActivityStatusRaw] = useState({});
  const setActivityStatus = (valOrFn) => {
    setActivityStatusRaw(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      return next;
    });
  };
  const [activityTypes, setActivityTypes] = useState([]);
  const [happeningNowEvents, setHappeningNowEvents] = useState([]);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [confirmDestroyHiveId, setConfirmDestroyHiveId] = useState(null);
  const [destroyDate, setDestroyDate] = useState(new Date().toISOString().split('T')[0]);
  const [destroyNotes, setDestroyNotes] = useState('');
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  // Lock body scroll when Terms dialog is open
  useEffect(() => {
    if (showTermsDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showTermsDialog]);

  // Auto-trigger welcome tour for first-time users (only after T&Cs accepted)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || loading || showTermsDialog) return;
    fetch(`${API}/tours/welcome`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(data => {
        if (data && !data.progress?.completed && !data.progress?.skipped) {
          setWelcomeTour(data);
          setTimeout(() => setShowWelcomeTour(true), 1500);
        }
      })
      .catch(() => {});
  }, [loading, showTermsDialog]);

  const [harvestSummary, setHarvestSummary] = useState(null);
  const [showAddHarvestDialog, setShowAddHarvestDialog] = useState(false);
  const [harvestRefreshKey, setHarvestRefreshKey] = useState(0);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // Status filter for hives list
  const [showAIReport, setShowAIReport] = useState(false);
  const [selectedHiveForAI, setSelectedHiveForAI] = useState(null);
  const [showAllHivesAIReport, setShowAllHivesAIReport] = useState(false);
  const [showLogMenu, setShowLogMenu] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  // Mite Treatment & Frame Scan states
  const [showTreatmentWizard, setShowTreatmentWizard] = useState(false);
  const [treatmentRefreshKey, setTreatmentRefreshKey] = useState(0);
  const [showPostTreatmentDialog, setShowPostTreatmentDialog] = useState(false);
  const [miteCountAction, setMiteCountAction] = useState(null);
  const [postMiteCount, setPostMiteCount] = useState('');
  const [postBeeSample, setPostBeeSample] = useState('300');
  const [showFrameScanDialog, setShowFrameScanDialog] = useState(false);
  // Weight alerts state
  const [weightAlerts, setWeightAlerts] = useState([]);
  const [showMiteCountDialog, setShowMiteCountDialog] = useState(false);
  const [activeTreatments, setActiveTreatments] = useState(null);
  const [allTreatmentPlans, setAllTreatmentPlans] = useState([]);
  const [treatmentContext, setTreatmentContext] = useState(null);
  // Bulk Yard Action states
  const [showBulkYardActionDialog, setShowBulkYardActionDialog] = useState(false);

  // Re-fetch active treatments when treatment data changes
  useEffect(() => {
    if (treatmentRefreshKey > 0 && token) {
      const headers = { Authorization: `Bearer ${token}` };
      axios.get(`${API}/treatment-plans/active`, { headers }).then(res => {
        setActiveTreatments(res.data || null);
      }).catch(() => {});
    }
  }, [treatmentRefreshKey]);

  const [bulkActionYard, setBulkActionYard] = useState(null);
  const [bulkActionHives, setBulkActionHives] = useState([]);
  const [showYardHistoryDialog, setShowYardHistoryDialog] = useState(false);
  const [historyYard, setHistoryYard] = useState(null);
  // Assign Crew states
  const [showAssignCrewDialog, setShowAssignCrewDialog] = useState(false);
  const [assignCrewHive, setAssignCrewHive] = useState(null);
  const [crewMembers, setCrewMembers] = useState([]);
  // Crew tasks for owner dashboard
  const [crewTasks, setCrewTasks] = useState([]);
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname, location.search]);
  const { user, isAdmin, isDemo, logout, token } = useAuth();
  const { hasFeature } = useFeatures();
  
  // Push notifications hook
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribePush, unsubscribe: unsubscribePush, loading: pushLoading } = usePushNotifications();
  
  // Get current view from URL params
  const searchParams = new URLSearchParams(location.search);
  const currentView = searchParams.get('view') || 'dashboard';

  // Handle startTour query param (from Help Center replay)
  const [paramTour, setParamTour] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slug = params.get('startTour');
    if (!slug) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    params.delete('startTour');
    window.history.replaceState({}, '', `/?${params.toString()}`);
    setTimeout(() => {
      fetch(`${API}/tours/${slug}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (r.ok) return r.json(); throw new Error(); })
        .then(data => { if (data) setParamTour(data); })
        .catch(() => {});
    }, 1000);
  }, [location.search]);

  const handleEditYard = (yard, e) => {
    if (e) e.stopPropagation();
    setEditingYard(yard);
    setShowEditYardDialog(true);
  };

  const handleBulkYardAction = (yardName, yardHives) => {
    setBulkActionYard(yardName);
    setBulkActionHives(yardHives);
    setShowBulkYardActionDialog(true);
  };

  const handleShowYardHistory = (yard) => {
    setHistoryYard(yard);
    setShowYardHistoryDialog(true);
  };

  const handleAIReport = (hive) => {
    if (isDemo) {
      toast.info('AI Progress Reports are not available in demo mode');
      return;
    }
    setSelectedHiveForAI(hive);
    setShowAIReport(true);
  };

  const toggleActivity = async (activityType) => {
    if (!token || isDemo) return;
    const field = activityType.field_name;
    if (!field) return;
    const newValue = !activityStatus[field];
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/auth/me/activity`, { field_name: field, enabled: newValue }, { headers });
      setActivityStatus(prev => ({ ...prev, [field]: newValue }));
      toast.success(newValue ? `${activityType.name} - visible to community!` : `${activityType.name} status cleared`);
      
      // Refresh happening now events
      const eventsRes = await axios.get(`${API}/auth/me/happening-now-events`, { headers });
      setHappeningNowEvents(eventsRes.data.events || []);
    } catch (err) {
      toast.error('Failed to update activity status');
    }
  };

  const confirmHappeningNowEvent = async (eventId, stillActive) => {
    if (!token || isDemo) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/auth/me/happening-now-events/${eventId}/confirm`, { still_active: stillActive }, { headers });
      
      if (stillActive) {
        toast.success('Activity confirmed - extended for another week!');
      } else {
        toast.success('Activity marked as ended');
      }
      
      // Refresh events and activity status
      const eventsRes = await axios.get(`${API}/auth/me/happening-now-events`, { headers });
      setHappeningNowEvents(eventsRes.data.events || []);
      
      // If ended, update local activity status
      if (!stillActive) {
        const event = happeningNowEvents.find(e => e.id === eventId);
        if (event) {
          setActivityStatus(prev => ({ ...prev, [event.field_name]: false }));
        }
      }
    } catch (err) {
      toast.error('Failed to update activity');
    }
  };

  // Handle navigation state from Swarm Traps "Add to Hives" button
  useEffect(() => {
    if (location.state?.openAddHive) {
      setShowAddDialog(true);
      if (location.state?.trapData) {
        setTrapDataForHive(location.state.trapData);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Listen for BeekeeperMap open event from sidebar
  useEffect(() => {
    const handleOpenBeekeeperMap = () => setShowBeekeeperMap(true);
    window.addEventListener('openBeekeeperMap', handleOpenBeekeeperMap);
    return () => window.removeEventListener('openBeekeeperMap', handleOpenBeekeeperMap);
  }, []);

  // Reset selected yard when navigating away from hives view
  useEffect(() => {
    if (currentView !== 'hives' && currentView !== 'inspections') {
      setSelectedYard(null);
    }
    // Refresh data when returning to main views (e.g., after editing a hive)
    if (token && ['hives', 'dashboard', 'analytics'].includes(currentView)) {
      fetchData();
    }
  }, [currentView]);

  useEffect(() => {
    if (token) {
      fetchData();
    } else if (isDemo) {
      fetchData();
    }
  }, [token]);

  // Listen for refresh events from child components (e.g., confirm destroyed)
  useEffect(() => {
    const handleRefresh = () => fetchData();
    window.addEventListener('refreshData', handleRefresh);
    return () => window.removeEventListener('refreshData', handleRefresh);
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // In demo mode without token, fetch demo data from unauthenticated endpoints
      if (!token && isDemo) {
        try {
          const [hivesRes, yardsRes, inspectionsRes, settingsRes] = await Promise.all([
            axios.get(`${API}/hives`),
            axios.get(`${API}/yards`),
            axios.get(`${API}/inspections`),
            axios.get(`${API}/app-settings`).catch(() => ({ data: null })),
          ]);
          setHives(hivesRes.data || []);
          setYards(yardsRes.data || []);
          setInspections(inspectionsRes.data || []);
          setSwarmTraps([]);
          setBeekeepers([]);
          setPlannedActions([]);
          setCompanyProfile({ company_name: 'Demo Apiary', name: 'Demo User' });
          if (settingsRes?.data) {
            setAppSettings(settingsRes.data);
            if (settingsRes.data.free_platform) setFreePlatform(true);
          }
        } catch { /* ignore */ }
        
        try {
          const alertsRes = await axios.get(`${API}/config/critical-alerts`);
          setCriticalAlertConfigs(alertsRes.data || []);
        } catch { /* ignore */ }
        
        setLoading(false);
        return;
      }
      
      // Helper: gracefully catch errors. Only auth/me 401 triggers logout.
      let authExpired = false;
      const safeCatch = (defaultData) => (err) => {
        if (err.response?.status === 401) {
          console.warn('401 from API:', err.config?.url);
        }
        return { data: defaultData };
      };
      
      const apiCalls = [
        axios.get(`${API}/hives`, { headers }).catch(safeCatch([])),
        axios.get(`${API}/yards`, { headers }).catch(safeCatch([])),
        axios.get(`${API}/inspections`, { headers }).catch(safeCatch([])),
        axios.get(`${API}/swarm-traps`, { headers }).catch(safeCatch([])),
        axios.get(`${API}/beekeepers`, { headers }).catch(safeCatch([])),
        axios.get(`${API}/app-settings`).catch(() => ({ data: null })),
        axios.get(`${API}/config/critical-alerts`).catch(() => ({ data: [] }))
      ];
      
      if (token) {
        // auth/me is the session-critical call — only this one triggers logout on 401
        apiCalls.push(axios.get(`${API}/auth/me?_t=${Date.now()}`, { headers }).catch((err) => {
          if (err.response?.status === 401) {
            authExpired = true;
          }
          return { data: null };
        }));
        apiCalls.push(axios.get(`${API}/planned-actions`, { headers }).catch(safeCatch([])));
        apiCalls.push(axios.get(`${API}/queen-rearing/batches`, { headers }).catch(safeCatch([])));
        apiCalls.push(axios.get(`${API}/harvests/summary`, { headers }).catch(safeCatch(null)));
        apiCalls.push(axios.get(`${API}/treatment-plans/active`, { headers }).catch(safeCatch(null)));
        apiCalls.push(axios.get(`${API}/yards/inspection-schedule`, { headers }).catch(safeCatch({})));
        apiCalls.push(axios.get(`${API}/treatment-plans`, { headers }).catch(safeCatch([])));
      }
      
      const results = await Promise.all(apiCalls);
      const [hivesRes, yardsRes, inspectionsRes, swarmTrapsRes, beekeepersRes, appSettingsRes, criticalAlertsRes] = results;
      const profileRes = token ? results[7] : null;
      const plannedActionsRes = token ? results[8] : null;
      const queenBatchesRes = token ? results[9] : null;
      const harvestSummaryRes = token ? results[10] : null;
      const activeTreatmentsRes = token ? results[11] : null;
      const scheduleRes = token ? results[12] : null;
      const allTreatmentPlansRes = token ? results[13] : null;
      
      // Only logout if the auth/me call specifically returned 401
      if (authExpired) {
        console.error('Session expired (auth/me returned 401) — logging out');
        localStorage.removeItem('token');
        window.location.href = '/';
        return;
      }
      
      // Store critical alert configurations
      const alertConfigs = criticalAlertsRes?.data || [];
      setCriticalAlertConfigs(alertConfigs);
      
      const hivesData = hivesRes.data.sort((a, b) => a.hive_number - b.hive_number);
      setHives(hivesData);
      setYards(yardsRes.data);
      setInspections(inspectionsRes.data);
      setSwarmTraps(swarmTrapsRes.data || []);
      setBeekeepers(beekeepersRes.data || []);
      setPlannedActions(plannedActionsRes?.data || []);
      setInspectionSchedule(scheduleRes?.data || {});
      setQueenBatches(queenBatchesRes?.data || []);
      setAppSettings(appSettingsRes?.data || null);
      if (appSettingsRes?.data?.free_platform) setFreePlatform(true);
      setHarvestSummary(harvestSummaryRes?.data || null);
      setActiveTreatments(activeTreatmentsRes?.data || null);
      const allPlansData = allTreatmentPlansRes?.data;
      setAllTreatmentPlans(Array.isArray(allPlansData) ? allPlansData : allPlansData?.plans || []);
      
      if (profileRes?.data) {
        setCompanyProfile(profileRes.data);
        
        try {
          const activityTypesRes = await axios.get(`${API}/config/happening_now`, { headers });
          const types = activityTypesRes.data.items || [];
          setActivityTypes(types);
          
          const statusObj = {};
          types.forEach(t => {
            const field = t.field_name;
            if (field) statusObj[field] = profileRes.data[field] || false;
          });
          setActivityStatus(statusObj);
          
          // Fetch happening now events for confirmation prompts
          const eventsRes = await axios.get(`${API}/auth/me/happening-now-events`, { headers });
          setHappeningNowEvents(eventsRes.data.events || []);
        } catch (err) {
          setActivityTypes([
            { name: 'Harvesting', icon: '🍯', color: 'amber', field_name: 'currently_harvesting' },
            { name: 'Feeding', icon: '🍽️', color: 'blue', field_name: 'currently_feeding' },
            { name: 'Mites Alert', icon: '🐛', color: 'red', field_name: 'mites_detected' },
          ]);
          setActivityStatus({
            currently_harvesting: profileRes.data.currently_harvesting || false,
            currently_feeding: profileRes.data.currently_feeding || false,
            mites_detected: profileRes.data.mites_detected || false
          });
        }
        
        if (!isDemo && (!profileRes.data.terms_accepted || profileRes.data.terms_version !== TERMS_VERSION)) {
          // Also check against the live published version from the API
          try {
            const versionRes = await axios.get(`${API}/legal-documents/current-version/terms`);
            const publishedVersion = versionRes.data?.version || TERMS_VERSION;
            if (!profileRes.data.terms_accepted || profileRes.data.terms_version !== publishedVersion) {
              setShowTermsDialog(true);
            }
          } catch {
            // Fallback to hardcoded version check if API fails
            if (!profileRes.data.terms_accepted || profileRes.data.terms_version !== TERMS_VERSION) {
              setShowTermsDialog(true);
            }
          }
        }
        
        // Fetch crew tasks for owner dashboard widget
        try {
          const crewTasksRes = await axios.get(`${API}/crew/tasks`, { headers });
          setCrewTasks(crewTasksRes.data || []);
          
          // Also fetch crew members for assignment dialog
          const crewRes = await axios.get(`${API}/crew/members`, { headers });
          setCrewMembers(crewRes.data || []);
          
          // Fetch notifications
          const notifRes = await axios.get(`${API}/notifications`, { headers });
          setNotifications(notifRes.data || []);
        } catch (err) {
          setCrewTasks([]);
        }
      } else {
        setCompanyProfile({ company_name: 'Demo Apiary', name: 'Demo User' });
      }
      
      if (isAdmin && token) {
        try {
          const pendingRes = await axios.get(`${API}/admin/listings/pending-count`, { headers });
          setPendingListingsCount(pendingRes.data.count || 0);
        } catch (err) {
        }
      }
      
      // Calculate critical hives and reminders using configurable alerts
      const critical = [];
      const remindersList = [];
      
      // Helper function to check if a field value triggers an alert
      const checkFieldTrigger = (inspection, alertConfig) => {
        const fieldName = alertConfig.field_name;
        const triggerValues = alertConfig.trigger_values || [];
        const fieldValue = inspection[fieldName];
        
        // Handle array fields (diseases, pests)
        if (Array.isArray(fieldValue) && fieldValue.length > 0) {
          // For array fields, trigger if any value matches or if array is non-empty and config says so
          if (triggerValues.some(tv => fieldValue.includes(tv))) return true;
          if (triggerValues.includes('any') || triggerValues.includes('Any')) return true;
          // Special case: if trigger includes array field name, check if array has items
          return false;
        }
        
        // Handle boolean fields
        if (typeof fieldValue === 'boolean') {
          return triggerValues.some(tv => 
            tv === fieldValue || 
            tv === String(fieldValue) || 
            (fieldValue && (tv === 'Yes' || tv === 'true' || tv === true))
          );
        }
        
        // Handle string fields
        if (typeof fieldValue === 'string') {
          return triggerValues.some(tv => 
            tv.toLowerCase() === fieldValue.toLowerCase() ||
            fieldValue.toLowerCase().includes(tv.toLowerCase())
          );
        }
        
        return false;
      };
      
      hivesData.forEach(hive => {
        const hiveInspections = inspectionsRes.data.filter(i => i.hive_id === hive.id);
        if (hiveInspections.length > 0) {
          const latest = hiveInspections[0];
          const alerts = [];
          const daysSince = Math.floor((new Date() - new Date(latest.date)) / (1000 * 60 * 60 * 24));
          
          // Check each enabled critical alert config
          alertConfigs.forEach(alertConfig => {
            if (alertConfig.is_enabled !== false) {
              if (checkFieldTrigger(latest, alertConfig)) {
                alerts.push({ 
                  type: alertConfig.field_name, 
                  label: alertConfig.name, 
                  severity: alertConfig.severity || 'critical',
                  category: alertConfig.category
                });
              }
            }
          });
          
          // Also check for diseases and pests arrays (special handling)
          if (latest.diseases && latest.diseases.length > 0) {
            const diseaseAlert = alertConfigs.find(a => a.field_name === 'diseases' && a.is_enabled !== false);
            if (diseaseAlert || alertConfigs.length === 0) {
              if (!alerts.some(a => a.type === 'diseases')) {
                alerts.push({ type: 'diseases', label: `Disease: ${latest.diseases.join(', ')}`, severity: 'critical' });
              }
            }
          }
          if (latest.pests && latest.pests.length > 0) {
            const pestAlert = alertConfigs.find(a => a.field_name === 'pests' && a.is_enabled !== false);
            if (pestAlert || alertConfigs.length === 0) {
              if (!alerts.some(a => a.type === 'pests')) {
                alerts.push({ type: 'pests', label: `Pests: ${latest.pests.join(', ')}`, severity: 'critical' });
              }
            }
          }
          
          if (alerts.length > 0) {
            critical.push({
              ...hive,
              alerts,
              reason: alerts.map(a => a.label).join(', '),
              latestInspectionId: latest.id,
              daysSinceInspection: daysSince
            });
          }
          
          // Build reminders from actions_to_take that haven't been completed
          const allActionsToTake = new Set();
          const allCompletedActions = new Set();
          
          hiveInspections.forEach(insp => {
            if (insp.actions_to_take) {
              insp.actions_to_take.forEach(a => allActionsToTake.add(a));
            }
            if (insp.actions) {
              insp.actions.forEach(a => allCompletedActions.add(a));
            }
          });
          
          const actionMapping = {
            'Mite Wash': ['Mite Wash'],
            'Split Hive': ['Split Hive'],
            'Requeen': ['Requeened'],
            'Add Feeders': ['Added Feeder'],
            'Add Nutrition': ['Added Nutrition', 'Add Nutrition'],
          };
          
          // Get planned actions for this hive to check for snoozed/scheduled items
          const hivePlannedActions = (plannedActionsRes?.data || []).filter(
            pa => (pa.hive_id === hive.id || (pa.hive_number && String(pa.hive_number).includes(String(hive.hive_number)))) && pa.status !== 'completed'
          );
          
          allActionsToTake.forEach(action => {
            const possibleCompletedNames = actionMapping[action] || [action];
            const isCompleted = possibleCompletedNames.some(name => allCompletedActions.has(name));
            
            // Check if this action has been snoozed/scheduled as a planned action
            const matchingPlanned = hivePlannedActions.find(pa => 
              pa.description === action || pa.description?.includes(action)
            );
            const isSnoozedToFuture = matchingPlanned?.date && new Date(matchingPlanned.date + 'T00:00:00') > new Date();
            
            if (!isCompleted && !isSnoozedToFuture) {
              const sourceInspection = hiveInspections.find(i => i.actions_to_take?.includes(action));
              remindersList.push({
                hiveId: hive.id,
                hiveNumber: hive.hive_number,
                yard: hive.yard,
                action,
                date: sourceInspection?.date || latest.date,
                assignedBeekeeper: sourceInspection?.actions_assigned_beekeeper || ''
              });
            }
          });
        }
      });
      
      setCriticalHives(critical);

      // Also add inactive hives with disease reasons (AFB, Dead Out) to critical alerts
      hivesData.forEach(hive => {
        if (hive.status === 'inactive' && hive.inactive_reason) {
          const reason = hive.inactive_reason.toLowerCase();
          const alreadyCritical = critical.find(c => c.id === hive.id);
          if (!alreadyCritical) {
            const alerts = [];
            if (reason.includes('afb')) {
              alerts.push({ type: 'afb', label: '🔥 AFB — Burn all equipment', severity: 'critical' });
            }
            if (reason.includes('dead out') || reason.includes('varroa') || reason.includes('shb') || reason.includes('wax moth')) {
              alerts.push({ type: 'dead_out', label: `Dead Out: ${hive.inactive_reason}`, severity: 'critical' });
            }
            if (alerts.length > 0) {
              critical.push({
                ...hive,
                alerts,
                reason: alerts.map(a => a.label).join(', '),
                daysSinceInspection: 0,
              });
            }
          }
        }
      });
      // Re-set with the added inactive alerts
      setCriticalHives([...critical]);
      setReminders(remindersList);
      
      // Fetch weight alerts
      try {
        const weightAlertsRes = await axios.get(`${API}/weight-alerts`, { headers });
        setWeightAlerts(weightAlertsRes.data.alerts || []);
      } catch (weightErr) {
      }
      
      // Check mite count records for high mite alerts
      try {
        const miteCountsRes = await axios.get(`${API}/mite-counts`, { headers });
        const miteCounts = Array.isArray(miteCountsRes.data) ? miteCountsRes.data : (miteCountsRes.data?.counts || []);
        // Group by hive, get latest per hive
        const latestByHive = {};
        miteCounts.forEach(mc => {
          const hid = mc.hive_id;
          if (!hid) return;
          if (!latestByHive[hid] || mc.date > latestByHive[hid].date) {
            latestByHive[hid] = mc;
          }
        });
        // Check for high mite percentages (>= 3%)
        Object.values(latestByHive).forEach(mc => {
          const pct = mc.percentage || mc.mite_percentage || (mc.mite_count && (mc.bee_count || mc.bee_sample) ? (mc.mite_count / (mc.bee_count || mc.bee_sample)) * 100 : null);
          if (pct !== null && pct >= 3) {
            const hive = hivesData.find(h => h.id === mc.hive_id);
            if (!hive) return;
            const alreadyCritical = critical.find(c => c.id === hive.id);
            const alertLabel = `High Mite Load: ${pct.toFixed(1)}% (${mc.mite_count || '?'} mites / ${mc.bee_count || mc.bee_sample || 300} bees)`;
            if (alreadyCritical) {
              if (!alreadyCritical.alerts.some(a => a.type === 'high_mite_count')) {
                alreadyCritical.alerts.push({ type: 'high_mite_count', label: alertLabel, severity: pct >= 5 ? 'critical' : 'warning' });
                alreadyCritical.reason += `, ${alertLabel}`;
              }
            } else {
              critical.push({
                ...hive,
                alerts: [{ type: 'high_mite_count', label: alertLabel, severity: pct >= 5 ? 'critical' : 'warning' }],
                reason: alertLabel,
                daysSinceInspection: null,
              });
            }
          }
        });
        setCriticalHives([...critical]);
      } catch (miteErr) {
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = async (version) => {
    setAcceptingTerms(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/auth/accept-terms`, { version }, { headers });
      setShowTermsDialog(false);
      setCompanyProfile(prev => ({ ...prev, terms_accepted: true, terms_version: version }));
      toast.success('Welcome to Thrive! 🐝');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Accept terms failed:', detail);
      toast.error(`Failed to accept terms: ${detail}. Please try again.`);
    } finally {
      setAcceptingTerms(false);
    }
  };

  const handleAddYard = async (yardData) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/yards`, yardData, { headers });
      toast.success('Apiary added successfully');
      fetchData();
      refreshLimits();
      setShowAddYardDialog(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'Plan limit reached. Please upgrade.', { duration: 6000, action: { label: 'Upgrade', onClick: () => navigate('/subscription') } });
      } else {
        toast.error('Failed to add apiary');
      }
    }
  };

  const handleAddHive = async (hiveData) => {
    if (!hiveData.yard) {
      toast.error('Please select an apiary first');
      return;
    }
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/hives`, hiveData, { headers });
      const newHiveId = response.data.id;
      
      // If this hive was created from a swarm trap, update the trap
      if (trapDataForHive?.id) {
        try {
          await axios.put(`${API}/swarm-traps/${trapDataForHive.id}`, {
            ...trapDataForHive,
            status: 'converted',
            converted_to_hive_id: newHiveId
          }, { headers });
          toast.success(`Hive added and trap "${trapDataForHive.name}" updated`);
        } catch (err) {
          toast.success('Hive added successfully');
        }
        setTrapDataForHive(null);
      } else {
        toast.success('Hive added successfully');
      }
      
      fetchData();
      refreshLimits();
      setShowAddDialog(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'Plan limit reached. Please upgrade.', { duration: 6000, action: { label: 'Upgrade', onClick: () => navigate('/subscription') } });
      } else {
        toast.error('Failed to add hive');
      }
    }
  };

  const handleInspectHive = (hive) => {
    navigate(`/?view=new-inspection&hiveId=${hive.id}`);
  };

  // Handle assigning crew to a hive task
  const handleAssignCrew = (hive) => {
    setAssignCrewHive(hive);
    setShowAssignCrewDialog(true);
  };


  const submitCrewAssignment = async (crewId, taskData) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const crew = beekeepers.find(b => b.id === crewId);
      
      await axios.post(`${API}/crew/tasks`, {
        assigned_to: crewId,
        hive_id: assignCrewHive.id,
        hive_number: assignCrewHive.hive_number,
        yard: assignCrewHive.yard,
        title: taskData.title || `Inspection: Hive #${assignCrewHive.hive_number}`,
        description: taskData.description || '',
        priority: taskData.priority || 'normal',
        due_date: taskData.due_date || new Date().toISOString().split('T')[0]
      }, { headers });
      
      toast.success(`Task assigned to ${crew?.name || 'crew member'}`);
      setShowAssignCrewDialog(false);
      setAssignCrewHive(null);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to assign task');
    }
  };

  // Handle marking a reminder as Done - records to hive inspection history
  const handleReminderDone = async (reminder, doneRecord) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Create a quick note inspection to record the completed action
      const inspectionData = {
        hive_id: reminder.hiveId,
        date: new Date().toISOString(),
        type_of_inspection: 'Quick Check',
        notes: `✅ Task Completed: ${reminder.action}\n\nCompleted on ${doneRecord.completedDate} at ${doneRecord.completedTime}`,
        actions: [reminder.action]
      };
      
      await axios.post(`${API}/inspections`, inspectionData, { headers });
      
      // Also mark any corresponding planned action as completed
      // Find planned actions matching this hive and action description
      const matchingActions = plannedActions.filter(pa => 
        pa.hive_id === reminder.hiveId && 
        pa.description === reminder.action &&
        pa.status !== 'completed'
      );
      
      // Mark all matching planned actions as completed
      for (const action of matchingActions) {
        try {
          await axios.put(`${API}/planned-actions/${action.id}/complete`, {}, { headers });
        } catch (err) {
        }
      }
      
      toast.success(`"${reminder.action}" marked as done and recorded on Hive ${reminder.hiveNumber}`);
      fetchData(); // Refresh to update reminders
    } catch (error) {
      toast.error('Failed to record action. Please try again.');
    }
  };

  // Handle snoozing a reminder - creates a planned action with the new date
  const handleReminderSnooze = async (reminder, newDate) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Create a planned action for the snoozed reminder
      await axios.post(`${API}/planned-actions`, {
        description: reminder.action,
        date: newDate,
        hive_id: reminder.hiveId,
        hive_number: reminder.hiveNumber,
        yard: reminder.yard,
        action_type: 'snoozed_reminder',
        notes: `Snoozed from original reminder`
      }, { headers });
      
      // Mark the original action as completed on the hive to remove it from reminders
      // by creating a quick inspection that records the snooze
      if (reminder.hiveId) {
        await axios.post(`${API}/inspections`, {
          hive_id: reminder.hiveId,
          date: new Date().toISOString(),
          type_of_inspection: 'Quick Check',
          notes: `⏰ Task Snoozed: ${reminder.action}\n\nRescheduled to ${new Date(newDate + 'T00:00:00').toLocaleDateString()}`,
          actions: [reminder.action]
        }, { headers });
      }
      
      // Parse date for display
      const [year, month, day] = newDate.split('-').map(Number);
      const displayDate = new Date(year, month - 1, day);
      toast.success(`"${reminder.action}" rescheduled to ${displayDate.toLocaleDateString()}`);
      fetchData(); // Refresh to update reminders and calendar
    } catch (error) {
      toast.error('Failed to snooze reminder. Please try again.');
    }
  };

  // Handle adding a planned action
  const handleAddPlannedAction = async (action) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const actionData = {
        description: action.description,
        title: action.title || action.description,
        date: action.date, // Already in YYYY-MM-DD format from PlannedActions component
        action_type: 'general',
        yard_id: action.yard_id || null,
        yard: action.yard || null,
        hive_id: action.hive_id || null,
        hive_number: action.hive_number || null
      };
      
      await axios.post(`${API}/planned-actions`, actionData, { headers });
      
      // Parse date for display - handle YYYY-MM-DD format
      const [year, month, day] = action.date.split('-').map(Number);
      const displayDate = new Date(year, month - 1, day);
      toast.success(`Action "${action.description}" added for ${displayDate.toLocaleDateString()}`);
      fetchData(); // Refresh to show updated actions
    } catch (error) {
      toast.error('Failed to add action. Please try again.');
    }
  };

  // Handle marking a planned action as done
  const handlePlannedActionDone = async (action) => {
    // If it's a treatment mite_count action, show the mite count dialog
    if (action.action_type === 'treatment' && action.task_id) {
      // Check if the related task is a mite_count type
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const planRes = await axios.get(`${API}/treatment-plans`, { headers });
        const plans = planRes.data?.plans || planRes.data || [];
        for (const plan of plans) {
          const task = (plan.tasks || []).find(t => t.id === action.task_id);
          if (task && task.type === 'mite_count' && task.status !== 'completed') {
            setMiteCountAction({ action, planId: plan.id, taskId: task.id });
            setPostMiteCount('');
            setPostBeeSample('300');
            setShowPostTreatmentDialog(true);
            return;
          }
        }
      } catch { /* fall through to normal completion */ }
    }
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { queued } = await sendOrQueue({ method: 'put', path: `/planned-actions/${action.id}/complete`, body: {}, kind: 'task_done', label: `Done · ${action.description || 'task'}` });
      if (queued) queuedToast('task completion'); else toast.success(`Action "${action.description}" marked as done`);
      fetchData();
    } catch (error) {
      toast.error('Failed to complete action. Please try again.');
    }
  };

  const handleDashboardMiteCountSubmit = async () => {
    if (!miteCountAction || !postMiteCount) return;
    const { action, planId, taskId } = miteCountAction;
    const miteNum = parseInt(postMiteCount);
    const beeNum = parseInt(postBeeSample) || 300;
    const percentage = parseFloat(((miteNum / beeNum) * 100).toFixed(1));
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { queued } = await sendOrQueue({ method: 'put', path: `/planned-actions/${action.id}/complete`, body: {}, kind: 'treatment_dose', label: `Post-treatment mite count · ${miteNum} mites`, followUps: [
        { method: 'put', path: `/treatment-plans/${planId}/tasks/${taskId}`, body: { status: 'completed', completed_at: new Date().toISOString() } },
        { method: 'put', path: `/treatment-plans/${planId}`, body: { post_treatment_mite_count: miteNum, post_treatment_bee_sample: beeNum, post_treatment_mite_percentage: percentage } },
      ] });
      if (queued) queuedToast('mite count'); else toast.success(`Post-treatment mite count recorded: ${miteNum} mites (${percentage}%)`);
      setShowPostTreatmentDialog(false);
      setMiteCountAction(null);
      fetchData();
      setTreatmentRefreshKey(k => k + 1);
    } catch (err) {
      toast.error('Failed to save mite count');
    }
  };

  // Handle removing a planned action
  const handleRemovePlannedAction = async (actionId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/planned-actions/${actionId}`, { headers });
      toast.success('Action removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove action');
    }
  };

  // Handle snoozing/rescheduling a planned action
  const handleActionSnooze = async (action, newDate) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/planned-actions/${action.id}`, { date: newDate }, { headers });
      
      // Parse date for display
      const [year, month, day] = newDate.split('-').map(Number);
      const displayDate = new Date(year, month - 1, day);
      toast.success(`Action rescheduled to ${displayDate.toLocaleDateString()}`);
      fetchData(); // Refresh to update the calendar and lists
    } catch (error) {
      toast.error('Failed to reschedule action. Please try again.');
    }
  };

  // Build queen rearing reminders from batches
  const queenReminders = queenBatches.filter(b => !['complete', 'archived', 'failed'].includes(b.status) && b.graft_date).flatMap(batch => {
    const events = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Parse graft_date as local date (not UTC)
    const gp = batch.graft_date.split('-');
    const graftDate = new Date(parseInt(gp[0]), parseInt(gp[1]) - 1, parseInt(gp[2]));

    const milestones = [
      { days: 1, id: 'finisher', action: 'Move cells to Finisher hive' },
      { days: 4, id: 'accepted', action: 'Check cell acceptance rate' },
      { days: 8, id: 'capped', action: 'Check cells - should be capped' },
      { days: 10, id: 'transfer', action: 'Transfer to incubator or mating NUCs' },
      { days: 16, id: 'emergence', action: 'Queens should be emerging' },
      { days: 23, id: 'mating', action: 'Mating flights begin' },
      { days: 28, id: 'laying', action: 'Check if queens are laying' },
    ];

    milestones.forEach(m => {
      const eventDate = new Date(graftDate);
      eventDate.setDate(eventDate.getDate() + m.days);
      if (eventDate >= today) {
        events.push({
          id: `queen-${m.id}-${batch.id}`,
          type: 'queen_rearing',
          action: m.action,
          date: eventDate.toISOString(),
          hiveId: null,
          hiveNumber: null,
          yard: null,
          batchId: batch.id,
          batchName: batch.batch_name || `Batch ${batch.id.slice(0,6)}`
        });
      }
    });
    
    return events;
  });

  // Filter hives by selected apiary
  const filteredHives = selectedYard ? hives.filter(h => h.yard === selectedYard) : hives;
  
  // Calculate stats - filtered by selected apiary
  // Exclude "ready" status hives from active count and overdue calculations
  const filteredActiveHives = filteredHives.filter(h => h.status !== 'inactive' && h.status !== 'ready').length;
  const filteredReadyHives = filteredHives.filter(h => h.status === 'ready').length;
  const filteredOverdueHives = filteredHives.filter(h => {
    // Exclude ready hives from overdue - they're empty and don't need inspection
    if (h.status === 'ready' || h.status === 'inactive') return false;
    // Get yard-specific seasonal schedule
    const yardSchedule = inspectionSchedule[h.yard] || inspectionSchedule[h.yard?.trim()] || {};
    // If inspections are paused for this yard's current season (e.g. winter), skip
    if (yardSchedule.inspections_paused) return false;
    const interval = yardSchedule.inspection_interval_days || 21;
    if (!h.last_inspection) return true;
    const daysSince = Math.floor((new Date() - new Date(h.last_inspection)) / (1000 * 60 * 60 * 24));
    return daysSince > interval;
  });
  const filteredCriticalHives = criticalHives.filter(h => (!selectedYard || h.yard === selectedYard));
  const filteredWeightAlerts = weightAlerts.filter(w => !selectedYard || w.yard === selectedYard);
  
  // Total critical count includes both inspection-based alerts AND weight loss alerts
  const totalCriticalCount = filteredCriticalHives.length + filteredWeightAlerts.length;
  
  // Combine hive reminders with queen rearing reminders
  const hiveReminders = reminders.filter(r => !selectedYard || r.yard === selectedYard);
  const allReminders = [...hiveReminders, ...queenReminders].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Unfiltered stats (for reference) - exclude ready hives
  const activeHives = hives.filter(h => h.status !== 'inactive' && h.status !== 'ready').length;
  const readyHives = hives.filter(h => h.status === 'ready').length;
  const overdueHives = hives.filter(h => {
    if (h.status === 'ready' || h.status === 'inactive') return false;
    // Get yard-specific schedule
    const yardSchedule = inspectionSchedule[h.yard] || inspectionSchedule[h.yard?.trim()] || {};
    // If inspections are paused for this yard's current season (e.g. winter), skip
    if (yardSchedule.inspections_paused) return false;
    const interval = yardSchedule.inspection_interval_days || 21;
    if (!h.last_inspection) return true;
    const daysSince = Math.floor((new Date() - new Date(h.last_inspection)) / (1000 * 60 * 60 * 24));
    return daysSince > interval;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-400 border-t-transparent mx-auto mb-4"></div>
          <div className="text-black text-lg font-medium">Loading your apiary...</div>
        </div>
      </div>
    );
  }

  // Mobile notification bell mini-component
  const MobileNotifBell = () => {
    const [count, setCount] = React.useState(0);
    React.useEffect(() => {
      const fetchCount = async () => {
        try {
          const tk = localStorage.getItem('token');
          if (!tk) return;
          const res = await axios.get(`${API}/notifications/diy/count`, { headers: { Authorization: `Bearer ${tk}` } });
          setCount(res.data?.count || 0);
        } catch { /* ignore */ }
      };
      fetchCount();
      const iv = setInterval(fetchCount, 30000);
      return () => clearInterval(iv);
    }, []);
    return (
      <button
        onClick={() => navigate('/diy-ideas')}
        className="relative p-2 rounded-lg hover:bg-stone-100 transition"
        data-testid="mobile-notification-bell"
      >
        <Bell className={`h-5 w-5 ${count > 0 ? 'text-amber-600' : 'text-stone-500'}`} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5 animate-pulse">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen honeycomb-bg" style={{ display: 'grid', gridTemplateColumns: '1fr', width: '100%' }}>
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block fixed top-0 left-0 h-screen z-40" style={{ width: '224px' }}>
        <Sidebar 
          companyProfile={companyProfile} 
          onLogout={logout} 
          isAdmin={isAdmin} 
          isDemo={isDemo}
          hasFeature={hasFeature}
          appVersion={versionInfo}
          hiddenMenus={appSettings?.hidden_menus || []}
          freePlatform={freePlatform}
        />
      </div>

      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-stone-200 shadow-sm px-4 py-3 flex items-center justify-between ${showMobileMenu ? 'z-[200]' : 'z-[100]'}`} data-testid="mobile-header">
        <div className="flex items-center gap-3">
          {companyProfile?.logo ? (
            <img
              src={`${API}/uploads/${companyProfile.logo}`}
              alt="Logo"
              className="w-8 h-8 rounded-lg object-contain bg-amber-100 p-1"
            />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center">
              <img src="/thrive-logo.png" alt="Thrive" className="w-8 h-8 object-contain" />
            </div>
          )}
          <span className="font-bold text-black">{companyProfile?.company_name || 'Thrive'}</span>
        </div>
        <div className="flex items-center gap-1">
          <MobileNotifBell />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="h-10 w-10"
            data-testid="mobile-menu-toggle"
          >
            {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:ml-56 min-w-0" style={{ minHeight: '100vh' }}>
        {/* New Version Banner */}
        {updateAvailable && (
          <div className="bg-amber-600 text-white px-4 py-3 flex items-center justify-center gap-3 text-sm font-medium z-50 sticky top-0" data-testid="update-banner">
            <span>Updating to the latest version...</span>
            <button
              onClick={refreshApp}
              className="bg-white text-amber-700 px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-amber-50 transition-colors animate-pulse"
              data-testid="update-refresh-btn"
            >
              Refresh Now
            </button>
          </div>
        )}
        {/* Desktop Header */}
        <div className="hidden lg:block">
          <DashboardHeader
            yards={yards}
            hives={hives}
            selectedYard={selectedYard}
            onYardChange={setSelectedYard}
            onScanClick={() => setShowQRScanner(true)}
            onAddApiary={handleAddYardClick}
            onAddHive={handleAddHiveClick}
            onAddBeekeeper={() => navigate('/beekeepers')}
            companyProfile={companyProfile}
            appSettings={appSettings}
          />
        </div>

        {/* Dashboard Content */}
        <div className="flex-1 min-w-0 p-4 lg:p-6 pb-24 lg:pb-20 mt-16 lg:mt-0 overflow-y-auto">
          {/* Demo Banner */}
          {isDemo && (
            <div className="bg-amber-100 border border-amber-300 text-black px-4 py-2 rounded-lg mb-4 text-sm font-medium text-center">
              👀 Demo Mode (Read-only) — <button onClick={() => navigate('/login')} className="underline font-bold">Sign in</button> to manage your apiaries
            </div>
          )}

          {/* Selected Apiary Banner */}
          {selectedYard && currentView === 'dashboard' && (
            <div className="bg-sky-100 border border-sky-300 text-black px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
              <span className="font-medium">
                Showing data for: <strong>{selectedYard}</strong>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedYard(null)}
                className="text-sky-700 hover:bg-sky-200"
              >
                Show All Apiaries
              </Button>
            </div>
          )}

          {/* Ad Banner — 728×90 Leaderboard (top of dashboard) */}
          {currentView === 'dashboard' && (
            <div className="flex justify-center mb-4">
              <div className="w-[728px] max-w-full">
                <AdCarousel placement="dashboard_banner" />
              </div>
            </div>
          )}

          {/* Stat Cards Row - 5 cards even across */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <StatCardHorizontal
              title="Active Hives"
              value={filteredActiveHives}
              icon="🐝"
              color="amber"
              onClick={() => navigate('/?view=hives')}
            />
            <StatCardHorizontal
              title="Critical alerts"
              value={totalCriticalCount}
              subtitle={totalCriticalCount > 0 ? `${totalCriticalCount} need action` : 'All clear'}
              icon="🚨"
              color="rose"
              onClick={() => navigate('/?view=health')}
            />
            <StatCardHorizontal
              title="Overdue"
              value={filteredOverdueHives.length}
              subtitle={filteredOverdueHives.length > 0 ? `${filteredOverdueHives.length} past due` : 'None'}
              icon="⏰"
              color="orange"
              onClick={() => navigate('/?view=inspections')}
            />
            <StatCardHorizontal
              title="Tasks"
              value={allReminders.length + plannedActions.filter(a => a.status !== 'completed').length}
              subtitle={`${allReminders.length} reminders, ${plannedActions.filter(a => a.status !== 'completed').length} scheduled`}
              icon="📋"
              color="indigo"
              onClick={() => navigate('/?view=tasks')}
            />
            <StatCardHorizontal
              title="Mite Control"
              value={(activeTreatments?.active_count || 0) + (activeTreatments?.scheduled_count || 0)}
              subtitle={activeTreatments?.active_count > 0 || activeTreatments?.scheduled_count > 0
                ? `${activeTreatments.active_count || 0} active, ${activeTreatments.scheduled_count || 0} scheduled`
                : 'No treatments'}
              icon="🐛"
              color="teal"
              onClick={() => navigate('/?view=treatments')}
            />
          </div>

          {/* Dynamic Content Based on View */}
          {currentView === 'dashboard' && (
            <>
              {/* Getting Started Checklist */}
              <GettingStartedChecklist />
              {token && !isDemo && (
                <div className="mb-4">
                  <QueenReminderAlerts token={token} max={3} />
                  <QueenlessAlerts token={token} />
                </div>
              )}
              {/* Seasonal Reminders */}
              <div className="mb-4">
                <SeasonalReminders isDemo={isDemo} userPlan={user?.subscription_plan || 'free'} onNavigate={(v) => navigate(`/?view=${v}`)} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start">
                {/* Left Column - Attention Center */}
                <AttentionCenter
                  criticalHives={filteredCriticalHives}
                  watchlistHives={[]}
                  overdueHives={filteredOverdueHives.map(h => {
                    const yardSched = inspectionSchedule[h.yard] || inspectionSchedule[h.yard?.trim()] || {};
                    const interval = yardSched.inspection_interval_days || 21;
                    return {
                    ...h,
                    daysSinceInspection: h.last_inspection 
                      ? Math.floor((new Date() - new Date(h.last_inspection)) / (1000 * 60 * 60 * 24))
                      : null,
                    daysOverdue: h.last_inspection
                      ? Math.floor((new Date() - new Date(h.last_inspection)) / (1000 * 60 * 60 * 24)) - interval
                      : null,
                    currentSeason: yardSched.current_season || '',
                    inspectionInterval: interval
                    };
                  })}
                  weightAlerts={weightAlerts}
                  yards={yards}
                  onInspect={handleInspectHive}
                  onAssignCrew={handleAssignCrew}
                  onConfirmDestroyed={(hiveId) => { setConfirmDestroyHiveId(hiveId); setDestroyDate(new Date().toISOString().split('T')[0]); setDestroyNotes(''); }}
                />

                {/* Right Column - Weather, This Week & Planned Actions */}
                <div className="space-y-6">
                  {/* Weather Summary Card */}
                  <WeatherCard yards={yards} userProfile={user} />
                  
                  <ThisWeek
                    reminders={allReminders}
                    plannedActions={plannedActions}
                    queenBatches={queenBatches}
                    onMarkDone={handleReminderDone}
                    onSnooze={handleReminderSnooze}
                    onActionDone={handlePlannedActionDone}
                    onActionSnooze={handleActionSnooze}
                  />
                  <PlannedActions 
                    actions={plannedActions}
                    yards={yards}
                    hives={hives}
                    queenBatches={queenBatches}
                    onAddAction={handleAddPlannedAction}
                    onMarkDone={handlePlannedActionDone}
                    onRemoveAction={handleRemovePlannedAction}
                  />
                  
                  {/* Crew Tasks Widget */}
                  {crewTasks.length > 0 && (
                    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-white">
                            <Users className="h-5 w-5" />
                            <h3 className="font-semibold">Crew Tasks</h3>
                            {/* Notification Bell */}
                            {notifications.filter(n => !n.read && n.type === 'crew_task_completed').length > 0 && (
                              <div className="relative ml-2">
                                <Bell className="h-5 w-5 text-yellow-300 animate-pulse" />
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center font-bold">
                                  {notifications.filter(n => !n.read && n.type === 'crew_task_completed').length}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Push Notification Toggle */}
                            {pushSupported && (
                              <button
                                onClick={async () => {
                                  if (pushSubscribed) {
                                    await unsubscribePush();
                                  } else {
                                    const success = await subscribePush();
                                    if (!success && Notification.permission === 'denied') {
                                      alert('Please enable notifications in your browser settings');
                                    }
                                  }
                                }}
                                disabled={pushLoading}
                                className={`p-1.5 rounded-full transition-colors ${
                                  pushSubscribed 
                                    ? 'bg-green-400/30 text-green-200 hover:bg-green-400/40' 
                                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                                }`}
                                title={pushSubscribed ? 'Push notifications enabled - click to disable' : 'Enable push notifications'}
                              >
                                {pushSubscribed ? (
                                  <BellRing className="h-4 w-4" />
                                ) : (
                                  <BellOff className="h-4 w-4" />
                                )}
                              </button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-white hover:bg-white/20"
                              onClick={() => navigate('/crew-management')}
                            >
                              View All
                            </Button>
                          </div>
                        </div>
                        
                        {/* Recent Completion Notifications */}
                        {notifications.filter(n => !n.read && n.type === 'crew_task_completed').length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/20">
                            <p className="text-white/80 text-xs mb-2">Recent Completions:</p>
                            <div className="space-y-1">
                              {notifications
                                .filter(n => !n.read && n.type === 'crew_task_completed')
                                .slice(0, 3)
                                .map(n => (
                                  <div 
                                    key={n.id}
                                    className="flex items-center justify-between bg-white/10 rounded px-2 py-1"
                                  >
                                    <span className="text-white/90 text-xs truncate flex-1">
                                      <CheckCircle className="h-3 w-3 inline mr-1 text-green-300" />
                                      {n.message}
                                    </span>
                                    <button
                                      data-testid={`mark-read-btn-${n.id}`}
                                      onClick={async () => {
                                        try {
                                          const token = localStorage.getItem('token');
                                          await axios.put(`${API}/notifications/${n.id}/read`, {}, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          setNotifications(prev => prev.map(notif => 
                                            notif.id === n.id ? {...notif, read: true} : notif
                                          ));
                                        } catch (err) {
                                        }
                                      }}
                                      className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/25 hover:bg-white/50 text-white text-xs font-medium transition-all whitespace-nowrap"
                                      title="Mark as read"
                                    >
                                      <Check className="h-3 w-3" />
                                      Read
                                    </button>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
                        {crewTasks.filter(t => t.status !== 'completed').slice(0, 5).map(task => {
                          const crew = crewMembers.find(c => c.id === task.assigned_to);
                          return (
                            <div key={task.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                  task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                  task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                  'bg-indigo-100 text-indigo-700'
                                }`}>
                                  {crew?.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div>
                                  <div className="font-medium text-stone-800 text-sm">{task.title}</div>
                                  <div className="text-xs text-stone-500">
                                    {crew?.name || 'Unassigned'} 
                                    {task.hive_number > 0 && ` • Hive ${task.hive_number}`}
                                    {task.yard && ` • ${task.yard}`}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {task.status === 'in_progress' ? (
                                  <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                    <Clock className="h-3 w-3" /> In Progress
                                  </span>
                                ) : task.priority === 'urgent' ? (
                                  <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
                                    <AlertTriangle className="h-3 w-3" /> Urgent
                                  </span>
                                ) : (
                                  <span className="text-xs text-stone-500 bg-stone-100 px-2 py-1 rounded-full">
                                    {task.due_date ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No due date'}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {crewTasks.filter(t => t.status !== 'completed').length === 0 && (
                          <div className="text-center text-stone-500 py-4">
                            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                            <p className="text-sm">All crew tasks completed!</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Monthly Calendar with Weather */}
              <div className="mb-6">
                <MonthlyCalendar 
                  reminders={allReminders}
                  plannedActions={plannedActions}
                  queenBatches={queenBatches}
                  treatmentPlans={allTreatmentPlans}
                  yards={yards}
                  onActionUpdate={fetchData}
                  onReminderUpdate={fetchData}
                />
              </div>
            </>
          )}

          {currentView === 'apiaries' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2"><MapPin className="h-6 w-6 text-amber-600" /> Apiaries</h2>
                <div className="flex items-center gap-2">
                  <PageTourButton pageView="apiaries" />
                {!isDemo && (
                  <Button
                    onClick={() => setShowAllHivesAIReport(true)}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-800 font-semibold"
                    data-testid="ai-apiaries-report"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    AI Summary
                  </Button>
                )}
                </div>
              </div>
              <ApiariesList
                yards={yards}
                hives={hives}
                onEdit={handleEditYard}
                onSelect={(yardName) => {
                  setSelectedYard(yardName);
                  navigate('/?view=hives');
                }}
                onAddApiary={handleAddYardClick}
                onShowHistory={handleShowYardHistory}
              />
            </div>
          )}

          {currentView === 'hives' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                  <Home className="h-6 w-6 text-amber-600" />
                  {selectedYard ? `Hives in ${selectedYard}` : 'All Hives'}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Status Filter Dropdown */}
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px] h-10 bg-white">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Hives</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="ready">Ready (Prepared)</SelectItem>
                      <SelectItem value="overdue">Inspection Overdue</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="destroyed">Destroyed</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isDemo && (
                    <Button
                      onClick={() => setShowAllHivesAIReport(true)}
                      className="bg-purple-100 hover:bg-purple-200 text-purple-800 font-semibold"
                      data-testid="ai-hives-report"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI Summary
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Status Filter Info */}
              {statusFilter !== 'all' && (
                <div className={`rounded-lg p-3 text-sm flex items-center justify-between ${
                  statusFilter === 'ready' ? 'bg-amber-50 border border-amber-200 text-amber-800' :
                  statusFilter === 'overdue' ? 'bg-red-50 border border-red-200 text-red-800' :
                  statusFilter === 'inactive' ? 'bg-stone-100 border border-stone-200 text-stone-600' :
                  statusFilter === 'destroyed' ? 'bg-stone-200 border border-stone-300 text-stone-700' :
                  'bg-green-50 border border-green-200 text-green-800'
                }`}>
                  <span>
                    {statusFilter === 'ready' && `📦 Showing ${hives.filter(h => h.status === 'ready').length} prepared hives ready for assignment`}
                    {statusFilter === 'overdue' && `⚠️ Showing ${filteredOverdueHives.length} hives needing inspection`}
                    {statusFilter === 'inactive' && `Showing ${hives.filter(h => h.status === 'inactive').length} inactive hives`}
                    {statusFilter === 'destroyed' && `🔒 Showing ${hives.filter(h => h.status === 'destroyed').length} destroyed hives (records preserved for compliance)`}
                    {statusFilter === 'active' && `✅ Showing ${hives.filter(h => h.status !== 'inactive' && h.status !== 'ready' && h.status !== 'destroyed').length} active hives`}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setStatusFilter('all')}
                    className="text-xs"
                  >
                    Clear filter
                  </Button>
                </div>
              )}
              
              <HivesList
                hives={
                  statusFilter === 'all' ? hives :
                  statusFilter === 'ready' ? hives.filter(h => h.status === 'ready') :
                  statusFilter === 'overdue' ? filteredOverdueHives :
                  statusFilter === 'inactive' ? hives.filter(h => h.status === 'inactive') :
                  statusFilter === 'destroyed' ? hives.filter(h => h.status === 'destroyed') :
                  statusFilter === 'active' ? hives.filter(h => h.status !== 'inactive' && h.status !== 'ready' && h.status !== 'destroyed') :
                  hives
                }
                yards={yards}
                selectedYard={selectedYard}
                onYardChange={setSelectedYard}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                title=""
                defaultGroupByYard={true}
                onAddHive={handleAddHiveClick}
                onBulkAction={handleBulkYardAction}
              />
            </div>
          )}

          {currentView === 'inspections' && (
            <div className="space-y-4">
              <HivesList
                hives={filteredOverdueHives}
                yards={yards}
                selectedYard={selectedYard}
                onYardChange={setSelectedYard}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                title="Overdue Inspections"
                defaultGroupByYard={true}
                onAddHive={handleAddHiveClick}
                onBulkAction={handleBulkYardAction}
              />
              <HivesList
                hives={hives.filter(h => !filteredOverdueHives.find(oh => oh.id === h.id))}
                yards={yards}
                selectedYard={selectedYard}
                onYardChange={setSelectedYard}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                title="Up to Date"
                defaultGroupByYard={true}
                onAddHive={handleAddHiveClick}
                onBulkAction={handleBulkYardAction}
              />
            </div>
          )}

          {currentView === 'tasks' && (
            <div className="space-y-6">
              <ThisWeek
                reminders={allReminders}
                plannedActions={plannedActions}
                queenBatches={queenBatches}
                onMarkDone={handleReminderDone}
                onSnooze={handleReminderSnooze}
                onActionDone={handlePlannedActionDone}
                onActionSnooze={handleActionSnooze}
              />
              <PlannedActions 
                actions={plannedActions}
                yards={yards}
                hives={hives}
                queenBatches={queenBatches}
                onAddAction={handleAddPlannedAction}
                onMarkDone={handlePlannedActionDone}
                onRemoveAction={handleRemovePlannedAction}
              />
              <MonthlyCalendar 
                reminders={allReminders}
                plannedActions={plannedActions}
                queenBatches={queenBatches}
                treatmentPlans={allTreatmentPlans}
                yards={yards}
              />
            </div>
          )}

          {currentView === 'health' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2"><Heart className="h-6 w-6 text-amber-600" /> Health Alerts</h2>
                {!isDemo && (
                  <Button
                    onClick={() => setShowAllHivesAIReport(true)}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-800 font-semibold"
                    data-testid="ai-health-report"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    AI Health Summary
                  </Button>
                )}
              </div>
              <AttentionCenter
                criticalHives={filteredCriticalHives}
                watchlistHives={[]}
                overdueHives={[]}
                weightAlerts={weightAlerts}
                yards={yards}
                onInspect={handleInspectHive}
                onAssignCrew={handleAssignCrew}
                onConfirmDestroyed={(hiveId) => { setConfirmDestroyHiveId(hiveId); setDestroyDate(new Date().toISOString().split('T')[0]); setDestroyNotes(''); }}
              />
            </div>
          )}

          {currentView === 'harvests' && (
            <UpgradeGate feature="harvest_logging" hasFeature={hasFeature} featureName="Harvest Tracking">
            <HarvestsView 
              token={token}
              hives={hives}
              yards={yards}
              summary={harvestSummary}
              onAddHarvest={() => setShowAddHarvestDialog(true)}
              onRefresh={fetchData}
              refreshKey={harvestRefreshKey}
            />
            </UpgradeGate>
          )}

          {currentView === 'sales' && (
            <UpgradeGate feature="record_sales" hasFeature={hasFeature} featureName="Sales Tracking">
            <SalesView 
              token={token}
              yards={yards}
            />
            </UpgradeGate>
          )}

          {currentView === 'treatments' && (
            <TreatmentPlansView
              token={token}
              hives={hives}
              yards={yards}
              onAddPlan={(context) => {
                if (context?.hive_id) {
                  const hiveObj = hives.find(h => h.id === context.hive_id);
                  if (hiveObj) {
                    setTreatmentContext({ hive: hiveObj, urgency: 'high' });
                  } else {
                    setTreatmentContext({ hive: { id: context.hive_id, hive_number: context.hive_number }, urgency: 'high' });
                  }
                }
                setShowTreatmentWizard(true);
              }}
              onRefresh={() => { fetchData(); setTreatmentRefreshKey(k => k + 1); }}
              refreshKey={treatmentRefreshKey}
              onOpenFrameScan={() => setShowFrameScanDialog(true)}
              onOpenMiteCount={() => setShowMiteCountDialog(true)}
              appSettings={appSettings}
            />
          )}

          {currentView === 'products' && (
            <ProductsView />
          )}

          {currentView === 'harvest-settings' && (
            <UpgradeGate feature="harvest_logging" hasFeature={hasFeature} featureName="Harvest Settings">
            <HarvestSettingsView />
            </UpgradeGate>
          )}

          {currentView === 'weight-config' && (
            <WeightConfigurationView />
          )}
          {currentView === 'season-settings' && (
            <SeasonSettings />
          )}
          {(currentView === 'donations' || currentView === 'donation-success') && (
            <Donations />
          )}
          {currentView === 'bulk-weights' && (
            <BulkWeightEntry hives={hives} yards={yards} inspections={inspections} />
          )}
          {currentView === 'my-hive-types' && (
            <ManageHiveTypes />
          )}

          {currentView === 'find-honey' && (
            <FindHoney />
          )}

          {currentView === 'find-bee-club' && (
            <FindBeeClub />
          )}

          {currentView === 'advertise' && (
            <AdvertiserDashboard />
          )}

          {currentView === 'crew' && (
            <CrewManagement />
          )}
          {currentView === 'team-chat' && (
            <TeamChat />
          )}
          {currentView === 'bulk-add-hives' && (
            <BulkAddHives />
          )}
          {currentView === 'bulk-split' && (
            <BulkSplit />
          )}
          {currentView === 'queen-rearing' && (
            <QueenRearing />
          )}
          {currentView === 'swarm-traps' && (
            <SwarmTraps />
          )}
          {currentView === 'swarm-collections' && (
            <SwarmCollectionsView />
          )}
          {currentView === 'swarm-collection-settings' && (
            <SwarmCollectionSettings />
          )}
          {currentView === 'analytics' && (
            <Analytics />
          )}
          {currentView === 'hive-comparison' && (
            <HiveComparison />
          )}
          {currentView === 'hive-costs' && (
            <HiveCosts />
          )}
          {currentView === 'knowledge' && (
            <KnowledgeLibrary />
          )}
          {currentView === 'marketplace' && (
            <Marketplace />
          )}
          {currentView === 'diy-ideas' && (
            <DIYIdeas />
          )}
          {currentView === 'builder-settings' && (
            <BuilderSettings />
          )}
          {currentView === 'hive-mind' && (
            <HiveMind />
          )}
          {currentView === 'my-profile' && (
            <MyProfile />
          )}
          {currentView === 'subscription' && (
            <Subscription />
          )}
          {currentView === 'help' && (
            <UserManual />
          )}
          {currentView === 'admin-users' && (
            <UserManagement />
          )}
          {currentView === 'admin-listings' && (
            <AdminListings />
          )}
          {currentView === 'admin-settings' && (
            <AdminSettings />
          )}
          {currentView === 'admin-plans' && (
            <AdminSubscriptionPlans />
          )}
          {currentView === 'admin-config' && (
            <AdminConfiguration />
          )}
          {currentView === 'admin-bugs' && (
            <AdminBugReports />
          )}
          {currentView === 'admin-legal' && (
            <AdminLegalEditor />
          )}
          {currentView === 'admin-reviews' && (
            <AdminReviews />
          )}
          {currentView === 'admin-honey-requests' && (
            <AdminHoneyRequests />
          )}
          {currentView === 'admin-tours' && (
            <AdminTours />
          )}
          {currentView === 'admin-ads' && (
            <AdminAds />
          )}
          {currentView === 'admin-bee-clubs' && (
            <AdminBeeClubs />
          )}
          {currentView === 'admin-swarm-reports' && (
            <AdminSwarmDashboard />
          )}
          {currentView === 'merch-store' && (
            <MerchStore isAdmin={user?.role === 'admin'} />
          )}

          {currentView === 'hive-detail' && searchParams.get('hiveId') && (
            <HiveDetail />
          )}
          {currentView === 'new-inspection' && searchParams.get('hiveId') && (
            <InspectionForm />
          )}
          {currentView === 'edit-inspection' && searchParams.get('hiveId') && (
            <InspectionForm editMode={true} />
          )}
          {currentView === 'inspection' && searchParams.get('inspectionId') && (
            <InspectionView />
          )}

          {currentView === 'find-beekeepers' && (
            <BeekeeperMap embedded={true} />
          )}

          {currentView === 'collection' && searchParams.get('collectionId') && (
            <CollectionDetail />
          )}
          {currentView === 'idea-detail' && searchParams.get('ideaId') && (
            <IdeaDetail />
          )}
          {currentView === 'builder' && searchParams.get('userId') && (
            <BuilderProfile />
          )}
        </div>

        {!isDemo && (
          <MobileQuickActions
            hives={hives}
            overdueIds={new Set(overdueHives.map(h => h.id))}
            onHarvest={() => setShowAddHarvestDialog(true)}
            onMenu={() => setShowMobileMenu(true)}
          />
        )}

        {/* Happening Now Bar - Desktop */}
        <div className="hidden lg:block">
          <HappeningNowBar 
            activityTypes={activityTypes}
            activityStatus={activityStatus}
            onToggle={toggleActivity}
            happeningNowEvents={happeningNowEvents}
            onConfirmEvent={confirmHappeningNowEvent}
            isDemo={isDemo}
          />
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-[150] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl overflow-y-auto pt-14">
            <Sidebar
              companyProfile={companyProfile}
              onLogout={() => { logout(); setShowMobileMenu(false); }}
              isAdmin={isAdmin}
              isDemo={isDemo}
              hasFeature={hasFeature}
              appVersion={versionInfo}
              hiddenMenus={appSettings?.hidden_menus || []}
              onNavigate={() => setShowMobileMenu(false)}
              freePlatform={freePlatform}
            />
          </div>
        </div>
      )}

      {/* Dialogs */}
      <AddYardDialog
        open={showAddYardDialog}
        onClose={() => setShowAddYardDialog(false)}
        onAdd={handleAddYard}
        beekeepers={beekeepers}
      />

      <AddHiveDialog
        open={showAddDialog}
        onClose={() => { setShowAddDialog(false); setTrapDataForHive(null); }}
        onAdd={handleAddHive}
        existingHives={hives}
        trapData={trapDataForHive}
      />

      <EditYardDialog
        open={showEditYardDialog}
        onClose={() => { setShowEditYardDialog(false); setEditingYard(null); }}
        yard={editingYard}
        onUpdate={fetchData}
        allYards={yards}
        hivesInYard={editingYard ? hives.filter(h => h.yard === editingYard.name) : []}
        beekeepers={beekeepers}
      />

      {showBeekeeperMap && (
        <BeekeeperMap onClose={() => setShowBeekeeperMap(false)} />
      )}

      {showTermsDialog && (
        <TermsAcceptanceDialog 
          onAccept={handleAcceptTerms}
          loading={acceptingTerms}
          isUpdate={companyProfile?.terms_accepted && companyProfile?.terms_version !== TERMS_VERSION}
          onSkip={() => setShowTermsDialog(false)}
        />
      )}

      <QRScanner
        open={showQRScanner}
        onClose={() => setShowQRScanner(false)}
      />

      <AIProgressReport
        open={showAIReport}
        onClose={() => { setShowAIReport(false); setSelectedHiveForAI(null); }}
        hive={selectedHiveForAI}
        token={token}
      />

      <AllHivesAIReport
        open={showAllHivesAIReport}
        onClose={() => setShowAllHivesAIReport(false)}
        token={token}
      />

      <AddHarvestDialog
        open={showAddHarvestDialog}
        onClose={() => setShowAddHarvestDialog(false)}
        onSuccess={() => { fetchData(); setHarvestRefreshKey(k => k + 1); }}
        hives={hives}
        yards={yards}
        token={token}
      />

      <TreatmentPlanWizard
        open={showTreatmentWizard}
        onClose={() => {
          setShowTreatmentWizard(false);
          setTreatmentContext(null);
        }}
        onSuccess={() => {
          fetchData();
          setTreatmentRefreshKey(k => k + 1);
          setTreatmentContext(null);
        }}
        hives={hives}
        yards={yards}
        token={token}
        initialContext={treatmentContext}
      />

      <FrameScanDialog
        open={showFrameScanDialog}
        onClose={() => setShowFrameScanDialog(false)}
        hives={hives}
        token={token}
        onScanComplete={(result) => {
          toast.success('Frame analyzed! Results saved.');
          fetchData();
        }}
        onStartTreatment={(context) => {
          setShowFrameScanDialog(false);
          setTreatmentContext(context);
          setShowTreatmentWizard(true);
        }}
      />

      <MiteCountDialog
        open={showMiteCountDialog}
        onClose={() => setShowMiteCountDialog(false)}
        hives={hives}
        token={token}
        onSuccess={fetchData}
      />

      {/* Bulk Yard Action Dialog */}
      <BulkYardActionDialog
        open={showBulkYardActionDialog}
        onOpenChange={setShowBulkYardActionDialog}
        yardName={bulkActionYard}
        hives={bulkActionHives}
        onSuccess={fetchData}
      />

      {/* Yard History Dialog */}
      <YardHistoryDialog
        open={showYardHistoryDialog}
        onOpenChange={setShowYardHistoryDialog}
        yard={historyYard}
      />

      {/* Assign Crew Dialog */}
      <Dialog open={showAssignCrewDialog} onOpenChange={setShowAssignCrewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Assign Crew to Hive #{assignCrewHive?.hive_number}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            submitCrewAssignment(formData.get('crew_id'), {
              title: formData.get('title'),
              description: formData.get('description'),
              priority: formData.get('priority'),
              due_date: formData.get('due_date')
            });
          }} className="space-y-4">
            <div>
              <Label>Assign To</Label>
              <Select name="crew_id" required>
                <SelectTrigger>
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  {beekeepers.filter(b => b.can_login || b.user_id).map(crew => (
                    <SelectItem key={crew.id} value={crew.id}>
                      {crew.name} {crew.email && `(${crew.email})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Task Title</Label>
              <Input 
                name="title" 
                defaultValue={`Inspect Hive #${assignCrewHive?.hive_number}`}
                placeholder="e.g., Full Inspection, Check for Disease"
              />
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Textarea 
                name="description" 
                placeholder="Additional instructions..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select name="priority" defaultValue="normal">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input 
                  type="date" 
                  name="due_date"
                  defaultValue={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAssignCrewDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-500 hover:bg-purple-600 text-white">
                Assign Task
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* App Footer */}
      <AppFooter />

      {/* Confirm Destroyed Dialog (triggered from AttentionCenter or Hive Cards) */}
      {confirmDestroyHiveId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDestroyHiveId(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-stone-800">Confirm Equipment Destroyed</h3>
                <p className="text-sm text-stone-500">
                  Hive #{hives.find(h => h.id === confirmDestroyHiveId)?.hive_number}
                </p>
              </div>
            </div>
            <p className="text-sm text-stone-600 mb-4">
              Confirm that all frames, boxes, bottom boards, lids, and comb from this hive have been burned/destroyed per AFB protocol.
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Date Destroyed</label>
                <input
                  type="date"
                  value={destroyDate}
                  onChange={(e) => setDestroyDate(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm"
                  data-testid="dashboard-destroy-date-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Notes (optional)</label>
                <textarea
                  value={destroyNotes}
                  onChange={(e) => setDestroyNotes(e.target.value)}
                  placeholder="e.g., Equipment burned at site. Witnessed by..."
                  className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm h-20 resize-none"
                  data-testid="dashboard-destroy-notes-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmDestroyHiveId(null)}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={async () => {
                  try {
                    await axios.put(
                      `${API}/hives/${confirmDestroyHiveId}/confirm-destroyed`,
                      { destroyed_date: destroyDate, notes: destroyNotes },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Hive confirmed as destroyed. Records preserved.');
                    setConfirmDestroyHiveId(null);
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to confirm destruction');
                  }
                }}
                data-testid="dashboard-confirm-destroy-btn"
              >
                Confirm Destroyed
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Treatment Mite Count Dialog (for planned actions / scheduled tasks) */}
      <Dialog open={showPostTreatmentDialog} onOpenChange={setShowPostTreatmentDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Post-Treatment Mite Count</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500 mb-3">Enter your mite wash results to see if the treatment was effective.</p>
          <div className="space-y-4">
            <div>
              <Label className="text-stone-700 font-medium">Mites Found</Label>
              <Input type="number" min="0" placeholder="Number of mites" value={postMiteCount}
                onChange={(e) => setPostMiteCount(e.target.value)} className="mt-1" autoFocus />
            </div>
            <div>
              <Label className="text-stone-700 font-medium">Bee Sample Size</Label>
              <Input type="number" min="1" placeholder="300" value={postBeeSample}
                onChange={(e) => setPostBeeSample(e.target.value)} className="mt-1" />
              <p className="text-xs text-stone-400 mt-1">Standard half-cup = ~300 bees</p>
            </div>
            {postMiteCount && (() => {
              const pct = (parseInt(postMiteCount) / (parseInt(postBeeSample) || 300)) * 100;
              const color = pct < 2 ? 'border-green-300 bg-green-50' : pct < 4 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50';
              const textColor = pct < 2 ? 'text-green-700' : pct < 4 ? 'text-amber-700' : 'text-red-700';
              const label = pct < 2 ? 'Treatment Effective' : pct < 4 ? 'Moderate — Consider Re-treating' : 'High — Re-treatment Needed';
              return (
                <div className={`rounded-lg p-3 border text-center ${color}`}>
                  <div className={`text-2xl font-bold ${textColor}`}>{pct.toFixed(1)}%</div>
                  <div className={`text-sm font-medium ${textColor} mt-1`}>{label}</div>
                </div>
              );
            })()}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPostTreatmentDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleDashboardMiteCountSubmit} disabled={!postMiteCount}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black">Save & Complete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome Tour Overlay */}
      {showWelcomeTour && welcomeTour && (
        <TourEngine
          tour={welcomeTour}
          onComplete={() => setShowWelcomeTour(false)}
          onSkip={() => setShowWelcomeTour(false)}
        />
      )}

      {/* Tour launched from Help Center */}
      {paramTour && (
        <TourEngine
          tour={paramTour}
          onComplete={() => setParamTour(null)}
          onSkip={() => setParamTour(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
